import { Link, usePage } from '@inertiajs/react'
import Seo from '../components/Seo'
import ExampleTest from '../components/ExampleTest'
import { buttonClass, eyebrowClass, secondaryButtonClass } from '../components/ui'
import type { SharedProps, SeoProps } from '../types'

const pillars = [
  { number: '01', title: 'Ask better questions.', label: 'Ask', status: 'In development', description: 'Flex, torque, weight, bend profile. An advisor designed to explain shafts and fitting in plain language, with evidence and room for uncertainty.' },
  { number: '02', title: 'Make sense of your numbers.', label: 'Upload', status: 'Available now', description: 'Upload Garmin R10 / R50 sessions with automatic recognition, or match columns and units for other monitors. Understand what was measured and what the data can tell you.' },
  { number: '03', title: 'Test before you change.', label: 'Test', status: 'In development', description: 'Turn a hunch into a fitting project: a hypothesis, a fair test, and a before-and-after comparison. “Within normal variation” is a useful result, too.' },
]

function LabCapabilities() {
  return (
    <section aria-label="The lab" className="border-t border-line py-12 sm:py-16">
      <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-end">
        <div>
          <p className={`${eyebrowClass} text-muted`}>What’s in the lab</p>
          <h2 className="mt-3 font-serif text-3xl tracking-tight sm:text-4xl">A better way to know your gear.</h2>
        </div>
        <span className="w-fit rounded-full border border-line px-3 py-1.5 text-xs font-medium text-muted">Upload is ready to use</span>
      </div>
      <div className="mt-8 grid gap-6 md:grid-cols-3 md:gap-8">
        {pillars.map(pillar => (
          <article aria-label={pillar.label} key={pillar.number} className="border-t border-line pt-5">
            <div className="flex items-center gap-3">
              <span className="font-mono text-xs text-muted">{pillar.number}</span>
              <p className={`${eyebrowClass} text-brand`}>{pillar.label}</p>
            </div>
            <p className="mt-4 text-xs font-medium text-brand">{pillar.status}</p>
            <h3 className="mt-3 font-serif text-2xl leading-tight">{pillar.title}</h3>
            <p className="mt-3 text-sm leading-7 text-muted">{pillar.description}</p>
          </article>
        ))}
      </div>
    </section>
  )
}

function Independence() {
  return (
    <section id="independence" aria-labelledby="independence-title" className="rounded-xl bg-ink px-6 py-9 text-white sm:px-10 sm:py-12">
      <div className="grid gap-8 md:grid-cols-[0.9fr_1.1fr] md:gap-12">
        <div>
          <p className={`${eyebrowClass} text-accent`}>Our independence promise</p>
          <h2 id="independence-title" className="mt-4 max-w-sm font-serif text-3xl leading-tight sm:text-4xl">The right answer might already be in your bag.</h2>
        </div>
        <div>
          <p className="max-w-lg text-base leading-7 text-white/85">We’re building a lab that answers to the golfer. These are the standards we’re building toward:</p>
          <ul className="mt-5 divide-y divide-white/20 text-sm">
            {['No affiliate links or sponsored placement.', 'Recommendations ranked by fit to your profile.', 'Every equipment spec traced to its source.', 'Keeping your current gear counts as a good outcome.'].map(promise => (
              <li key={promise} className="flex gap-3 py-3"><span aria-hidden="true" className="text-accent">↗</span>{promise}</li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  )
}

export default function Home() {
  const { current_user, usage, seo } = usePage<SharedProps & { seo: SeoProps }>().props

  if (current_user) {
    return (
      <>
        <Seo seo={seo} title="Your lab" />
        <section className="pb-12">
          <Link href="/launch_sessions/new" className={`${buttonClass} mb-6 sm:float-right sm:ml-6`}>Upload a session</Link>
          <p className={`${eyebrowClass} text-muted`}>Your workbench</p>
          <h1 className="mt-4 break-words font-serif text-4xl tracking-tight sm:text-5xl">{current_user.name?.trim() ? `Welcome, ${current_user.name}.` : 'Welcome to your lab.'}</h1>
          <p className="mt-4 max-w-2xl text-base leading-7 text-muted">Start with what you know about your game. We’re building the tools to help you explore the rest.</p>
          <div className="mt-8 grid gap-5 md:grid-cols-[1.35fr_1fr]">
            <article className="rounded-xl border border-line bg-surface p-6 sm:p-8">
              <span className="rounded-full bg-soft px-3 py-1 text-xs font-medium text-brand">Available now</span>
              <h2 className="mt-5 font-serif text-3xl">My game</h2>
              <p className="mt-3 max-w-md text-sm leading-7 text-muted">Your swing, your current setup, and what you want to improve. Add what you know; everything is optional.</p>
              <Link href="/profile/edit" className={`${buttonClass} mt-6`}>Update my game profile</Link>
            </article>
            <article className="rounded-xl border border-line bg-soft p-6 sm:p-8">
              <p className={`${eyebrowClass} text-muted`}>Daily advisor allowance</p>
              {usage ? (
                <p aria-label="Advisor messages remaining today" className="mt-5 font-mono text-4xl tabular-nums">{usage.remaining_today} <span className="text-lg text-muted">of {usage.daily_limit}</span></p>
              ) : <p className="mt-5 text-xl">Allowance unavailable</p>}
              {usage && <p className="mt-2 text-sm text-muted">messages remaining today</p>}
              <p className="mt-6 border-t border-line pt-4 text-sm leading-6 text-muted">The advisor is in development. Messaging isn’t available yet.</p>
            </article>
          </div>
        </section>
        <LabCapabilities />
        <Independence />
      </>
    )
  }

  return (
    <>
      <Seo seo={seo} />
      <section className="grid items-center gap-10 pt-2 pb-12 md:grid-cols-[1.1fr_0.9fr] md:gap-12 sm:pt-5 sm:pb-16">
        <div>
          <p className={`${eyebrowClass} text-brand`}>The independent golf equipment lab</p>
          <h1 className="mt-5 font-serif text-[2.9rem] leading-[1.04] tracking-tight sm:text-6xl lg:text-[4.5rem]">Less guesswork.<br /><span className="italic text-brand">More understanding.</span></h1>
          <p className="mt-6 max-w-lg text-base leading-7 text-muted sm:text-lg sm:leading-8">Confused by shafts? Sitting on a pile of launch-monitor data? Get to know your equipment, ask better questions, and test changes with evidence.</p>
          <div className="mt-7 flex flex-wrap gap-3">
            <Link href="/registration/new" className={buttonClass}>Create a free account <span aria-hidden="true">↗</span></Link>
            <Link href="/session/new" className={secondaryButtonClass}>Sign in</Link>
          </div>
          <p className="mt-4 max-w-sm text-xs leading-5 text-muted">Your golf profile and session uploads are available today. The advisor and guided fitting projects are in development.</p>
        </div>
        <ExampleTest />
      </section>
      <div className="flex flex-wrap items-center justify-between gap-x-6 gap-y-3 border-y border-line py-5 text-sm text-muted">
        <span className="font-medium text-brand">Your game comes first.</span>
        <span>No affiliate links</span>
        <span>No sponsored placement</span>
        <span>Evidence over equipment hype</span>
      </div>
      <section id="approach" aria-labelledby="approach-title" className="py-12 sm:py-16">
        <div className="grid gap-8 md:grid-cols-[0.9fr_1.1fr] md:gap-12">
          <div>
            <p className={`${eyebrowClass} text-muted`}>The approach</p>
            <h2 id="approach-title" className="mt-3 max-w-sm font-serif text-3xl leading-tight sm:text-4xl">Start with your game.<br />Follow the evidence.</h2>
          </div>
          <ol className="space-y-6">
            {[
              ['Build your starting point', 'Create an account and add your current gear, swing details and goals. No need to know every number.', 'Available now'],
              ['Bring your session', 'Upload a Garmin R10 / R50 CSV or match columns from another monitor. Review shots and compare your sessions.', 'Available now'],
              ['Make a considered change', 'Try one change at a time. Compare results before deciding whether to keep it.', 'In development'],
            ].map(([title, description, status], index) => (
              <li key={title} className="flex gap-5">
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-line font-mono text-xs">{index + 1}</span>
                <div>
                  <h3 className="text-base font-semibold">{title}</h3>
                  <p className="mt-1 text-sm leading-6 text-muted">{description}</p>
                  <p className={`${eyebrowClass} mt-2 text-brand`}>{status}</p>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </section>
      <LabCapabilities />
      <Independence />
      <div className="flex flex-col justify-between gap-5 py-10 sm:flex-row sm:items-center">
        <p className="font-serif text-2xl">Start with the golfer. That’s you.</p>
        <Link href="/registration/new" className={secondaryButtonClass}>Join the lab <span aria-hidden="true">↗</span></Link>
      </div>
    </>
  )
}
